/**
 * DRIVER TEXT — the plain-text job sheet a dispatcher pastes into a message to
 * the driver (Dispatch → "Copy text for driver"). Sending comes later
 * (RingCentral); today it is copied.
 *
 * CONTRACTOR SIDE ONLY. Every rate and amount comes from the contractor
 * estimate (calcContractorEstimate), never the client's prices — the same
 * privacy rule as the rest of the Dispatch tab.
 *
 * A line whose value is empty is left out; a section left with nothing is left
 * out too. Pure: no fetches, so the format can be checked in isolation.
 */

import { formatPhone } from './format';
import { isLongDistance } from './moveType';
import type { ContractorCalc } from './estimate';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** 1 → "1st", 22 → "22nd", 13 → "13th". */
const ordinal = (n: number) => {
  const t = n % 100;
  const s = t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${s}`;
};

/** 'YYYY-MM-DD' (or a timestamp) → [year, monthIndex, day], or null. Read as a calendar day, never shifted by a timezone. */
const ymd = (v: unknown): [number, number, number] | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v ?? ''));
  return m ? [Number(m[1]), Number(m[2]) - 1, Number(m[3])] : null;
};

/**
 * "September 18th", "September 18th - 25th", "September 28th - October 3rd";
 * a range crossing a year names both years. '' when neither date is set.
 */
export function dateRangeText(start: unknown, end: unknown): string {
  const a = ymd(start);
  const b = ymd(end);
  const one = a ?? b;
  if (!one) return '';
  const day = (d: [number, number, number]) => `${MONTHS[d[1]]} ${ordinal(d[2])}`;
  if (!a || !b || (a[0] === b[0] && a[1] === b[1] && a[2] === b[2])) return day(one);
  if (a[0] !== b[0]) return `${day(a)}, ${a[0]} - ${day(b)}, ${b[0]}`;
  if (a[1] === b[1]) return `${day(a)} - ${ordinal(b[2])}`;
  return `${day(a)} - ${day(b)}`;
}

/** $120, $120.50, -$40 — whole dollars without cents. */
const money = (n: number) => {
  const abs = Math.abs(n);
  const s = Number.isInteger(Math.round(abs * 100) / 100)
    ? Math.round(abs).toLocaleString('en-CA')
    : abs.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}$${s}`;
};

/** A rate as entered, at least two decimals: 1.1 → "$1.10", 0.855 → "$0.855". */
const rateText = (n: number) => {
  const dec = (String(n).split('.')[1] ?? '').length;
  return `$${n.toFixed(Math.max(2, Math.min(dec, 4)))}`;
};

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
const lbs = (n: number) => `${Math.round(n).toLocaleString('en-CA')} LBs`;
const present = (v: unknown) => v != null && String(v).trim() !== '';

export interface DriverTextInput {
  lead: any;
  /** The contractor estimate as Dispatch computes it. */
  calc: ContractorCalc;
  /** Local hourly: the crew on the client estimate's base line (head count, trucks). Final crew wins when the invoice set one. */
  crew?: { movers: number | null; trucks: number | null } | null;
}

export function buildDriverText({ lead, calc, crew }: DriverTextInput): string {
  const ld = isLongDistance(lead);
  const sections: { title: string; blocks: string[][] }[] = [];
  const nonEmpty = (lines: (string | null | undefined | false)[]) => lines.filter((l): l is string => !!l && l.trim() !== '');

  // Client Information
  sections.push({
    title: 'Client Information -',
    blocks: [nonEmpty([
      `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim(),
      formatPhone(lead.phone),
      lead.email,
    ])],
  });

  // Pick up: LD — the confirmed day once the job is confirmed, else the pickup
  // window, else the move date. Local — the move date (there is no move-time field).
  const pickupWhen = ld
    ? (lead.job_confirmed && lead.move_date ? dateRangeText(lead.move_date, null) : '') ||
      dateRangeText(lead.pickup_start, lead.pickup_end) ||
      dateRangeText(lead.move_date, null)
    : dateRangeText(lead.move_date, null);
  sections.push({ title: 'Pick up -', blocks: [nonEmpty([pickupWhen]), nonEmpty([lead.origin_address])] });

  // Payment — contractor side only.
  const base = calc.items.find((l) => l.source === 'base');
  const others = calc.items.filter((l) => l.source !== 'base' && Math.round(l.net * 100) !== 0);
  const extras = others.map((l) => `${money(l.net)} ${l.desc}`.trim());
  const payment: string[][] = [];
  if (base?.rateKind === 'hourly') {
    const movers = present(base.crewSize) ? Number(base.crewSize) : crew?.movers ?? null;
    const trucks = crew?.trucks ?? null;
    const crewLine = [movers ? plural(movers, 'mover') : '', trucks ? plural(trucks, 'truck') : ''].filter(Boolean).join(', ');
    payment.push(nonEmpty([base.rate ? `${money(base.rate)} / HR` : '', crewLine, ...extras]));
  } else {
    const clientMin = Number(lead.minimum_weight ?? 2000);
    const minW = lead.contractor_min_overridden ? Number(lead.contractor_minimum_weight ?? clientMin) : clientMin;
    const est = Number(lead.weight_lbs);
    payment.push(nonEmpty([minW > 0 ? `${lbs(minW)} Minimum` : '']));
    if (present(lead.weight_lbs) && est > 0) payment.push(['Estimated Weight', lbs(est)]);
    // Per unit, as drivers read it: "$0.96 / LB" (a cubic-foot tariff: "/ CUFT").
    payment.push(nonEmpty([base && base.rate ? `${rateText(base.rate)} / ${(base.unitLabel ?? 'lb').toUpperCase()}` : '', ...extras]));
  }
  sections.push({ title: 'Payment :', blocks: payment });

  // Delivery: LD — the delivery window. Local — no window; the address only.
  const deliveryWhen = ld ? dateRangeText(lead.dropoff_start, lead.dropoff_end) : '';
  sections.push({ title: 'Delivery -', blocks: [nonEmpty([deliveryWhen, lead.destination_address])] });

  return sections
    .map((s) => ({ title: s.title, blocks: s.blocks.filter((b) => b.length > 0) }))
    .filter((s) => s.blocks.length > 0)
    .map((s) => [s.title, ...s.blocks.map((b) => b.join('\n'))].join('\n\n'))
    .join('\n\n');
}
